import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

function ChevronIcon({ open }) {
  return (
    <svg
      className={`clr-icon clr-table-filter-chevron${open ? ' is-open' : ''}`}
      width="18"
      height="18"
      viewBox="0 0 12 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1 6.5L6 1.5L11 6.5"
        stroke="#1C2B2A"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

ChevronIcon.propTypes = {
  open: PropTypes.bool.isRequired,
};

function FilterCheckbox({ id, label, checked, onChange }) {
  return (
    <label className="clr-table-filter-option" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        className="clr-table-filter-checkbox"
        checked={checked}
        onChange={onChange}
      />
      <span className="clr-table-filter-option-label">{label}</span>
    </label>
  );
}

FilterCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const OPTION_SHAPE = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

const EMPTY_VALUE = {
  payers: [],
  statuses: [],
  referralTypes: [],
  organizations: [],
};

function countSelectedFilters(filters = EMPTY_VALUE) {
  return (
    (filters.payers?.length || 0)
    + (filters.statuses?.length || 0)
    + (filters.referralTypes?.length || 0)
    + (filters.organizations?.length || 0)
  );
}

function normalizeFilters(filters) {
  return {
    payers: filters?.payers || [],
    statuses: filters?.statuses || [],
    referralTypes: filters?.referralTypes || [],
    organizations: filters?.organizations || [],
  };
}

function FilterSection({
  title,
  sectionKey,
  expanded,
  onToggle,
  options,
  selected,
  onToggleValue,
  emptyLabel,
}) {
  return (
    <section className="clr-table-filter-section">
      <button
        type="button"
        className="clr-table-filter-section__toggle"
        aria-expanded={expanded}
        onClick={() => onToggle(sectionKey)}
      >
        <span className="clr-table-filter-section__title">{title}</span>
        <ChevronIcon open={expanded} />
      </button>
      {expanded && (
        <div className="clr-table-filter-section__options">
          {options.length === 0 ? (
            <div className="clr-table-filter-empty">{emptyLabel}</div>
          ) : (
            options.map((option) => {
              const value = String(option.value);
              return (
                <FilterCheckbox
                  key={`${sectionKey}-${value}`}
                  id={`clr-filter-${sectionKey}-${value}`}
                  label={option.label}
                  checked={selected.includes(value)}
                  onChange={() => onToggleValue(sectionKey, value)}
                />
              );
            })
          )}
        </div>
      )}
    </section>
  );
}

FilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  sectionKey: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(OPTION_SHAPE).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleValue: PropTypes.func.isRequired,
  emptyLabel: PropTypes.string.isRequired,
};

function ClrTableFilterMenu({
  payers = [],
  statuses = [],
  referralTypes = [],
  organizations = [],
  value,
  onApply,
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({
    payer: true,
    status: true,
    referralType: true,
    organization: true,
  });
  const [draft, setDraft] = useState(normalizeFilters(value));
  const rootRef = useRef(null);

  const applied = useMemo(() => normalizeFilters(value), [value]);
  const draftCount = countSelectedFilters(draft);
  const appliedCount = countSelectedFilters(applied);
  const hasFilters = draftCount > 0 || appliedCount > 0;

  useEffect(() => {
    if (!open) return undefined;

    setDraft(normalizeFilters(value));

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, value]);

  const toggleExpanded = (sectionKey) => {
    setExpanded((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const toggleValue = (sectionKey, nextValue) => {
    const fieldMap = {
      payer: 'payers',
      status: 'statuses',
      referralType: 'referralTypes',
      organization: 'organizations',
    };
    const field = fieldMap[sectionKey];
    setDraft((prev) => {
      const list = prev[field] || [];
      return {
        ...prev,
        [field]: list.includes(nextValue)
          ? list.filter((item) => item !== nextValue)
          : [...list, nextValue],
      };
    });
  };

  const handleReset = () => {
    if (!hasFilters) return;
    const cleared = { ...EMPTY_VALUE };
    setDraft(cleared);
    onApply?.(cleared);
  };

  const handleApply = () => {
    if (draftCount === 0) return;
    onApply?.(normalizeFilters(draft));
    setOpen(false);
  };

  return (
    <div className={`clr-table-filter${open ? ' is-open' : ''}${appliedCount > 0 ? ' has-applied' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`clr-table-filter-trigger${appliedCount > 0 ? ' is-active' : ''}`}
        aria-label="Filter"
        title="Filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <svg className="clr-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M7.50022 15C7.50015 15.1394 7.53893 15.276 7.61218 15.3946C7.68544 15.5131 7.79029 15.609 7.91497 15.6713L9.41497 16.4213C9.52934 16.4784 9.65642 16.5054 9.78415 16.4996C9.91188 16.4938 10.036 16.4555 10.1447 16.3882C10.2535 16.3209 10.3432 16.227 10.4054 16.1153C10.4676 16.0036 10.5003 15.8779 10.5002 15.75V10.5C10.5004 10.1283 10.6386 9.76988 10.888 9.49425L16.3052 3.5025C16.4023 3.39492 16.4662 3.26151 16.489 3.11841C16.5119 2.97531 16.4929 2.82865 16.4341 2.69616C16.3754 2.56366 16.2796 2.45102 16.1582 2.37184C16.0368 2.29266 15.8951 2.25034 15.7502 2.25H2.25022C2.10517 2.25005 1.96326 2.29216 1.84166 2.37123C1.72005 2.45029 1.62399 2.56292 1.5651 2.69547C1.5062 2.82802 1.48701 2.97481 1.50984 3.11804C1.53267 3.26128 1.59654 3.39482 1.69372 3.5025L7.11247 9.49425C7.36187 9.76988 7.50005 10.1283 7.50022 10.5V15Z"
            stroke="#1C2B2A"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {appliedCount > 0 ? (
          <span className="clr-table-filter-badge">{appliedCount}</span>
        ) : null}
      </button>

      {open && (
        <div className="clr-table-filter-menu" role="dialog" aria-label="Filter by">
          <div className="clr-table-filter-menu__header">Filter by</div>

          <div className="clr-table-filter-menu__body">
            <FilterSection
              title="Payer"
              sectionKey="payer"
              expanded={expanded.payer}
              onToggle={toggleExpanded}
              options={payers}
              selected={draft.payers}
              onToggleValue={toggleValue}
              emptyLabel="No payers available"
            />
            <FilterSection
              title="Referral status"
              sectionKey="status"
              expanded={expanded.status}
              onToggle={toggleExpanded}
              options={statuses}
              selected={draft.statuses}
              onToggleValue={toggleValue}
              emptyLabel="No statuses available"
            />
            <FilterSection
              title="Referral type"
              sectionKey="referralType"
              expanded={expanded.referralType}
              onToggle={toggleExpanded}
              options={referralTypes}
              selected={draft.referralTypes}
              onToggleValue={toggleValue}
              emptyLabel="No referral types available"
            />
            <FilterSection
              title="Organization"
              sectionKey="organization"
              expanded={expanded.organization}
              onToggle={toggleExpanded}
              options={organizations}
              selected={draft.organizations}
              onToggleValue={toggleValue}
              emptyLabel="No organizations available"
            />
          </div>

          <div className="clr-table-filter-menu__footer">
            <button
              type="button"
              className={`clr-table-filter-reset${hasFilters ? ' is-active' : ''}`}
              onClick={handleReset}
              disabled={!hasFilters}
            >
              Reset
            </button>
            <button
              type="button"
              className={`clr-table-filter-apply${draftCount > 0 ? ' is-active' : ''}`}
              onClick={handleApply}
              disabled={draftCount === 0}
            >
              {draftCount > 0 ? `Apply (${draftCount})` : 'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

ClrTableFilterMenu.propTypes = {
  payers: PropTypes.arrayOf(OPTION_SHAPE),
  statuses: PropTypes.arrayOf(OPTION_SHAPE),
  referralTypes: PropTypes.arrayOf(OPTION_SHAPE),
  organizations: PropTypes.arrayOf(OPTION_SHAPE),
  value: PropTypes.shape({
    payers: PropTypes.arrayOf(PropTypes.string),
    statuses: PropTypes.arrayOf(PropTypes.string),
    referralTypes: PropTypes.arrayOf(PropTypes.string),
    organizations: PropTypes.arrayOf(PropTypes.string),
  }),
  onApply: PropTypes.func,
};

export default ClrTableFilterMenu;
